import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useAuthStore } from '@adaptcx/shared/stores/auth'
import { usePermissions } from '@adaptcx/shared'
import { assistConfigService, type AssistTenantConfig, type AssistConfigUpdate, type AssistMode } from '../services/assistConfig'

export const useAssistConfigStore = defineStore('assistConfig', () => {
  const config = ref<AssistTenantConfig | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)

  // --- Mode computeds ---
  const assistMode = computed<AssistMode>(() => config.value?.assist_mode ?? 'omni')
  const isOmniAssist = computed(() => assistMode.value === 'omni')
  const isGoogleNative = computed(() => assistMode.value === 'google_native')

  // --- Role computeds ---
  // Phase 2.23.g — delegate the underlying perm/role checks to the
  // shared usePermissions composable (lives in @adaptcx/shared) so
  // every portal (agent-assist, fax, operations, tenant-admin) reads
  // RBAC state through one API.
  //
  // The shape of this store's role API is preserved verbatim for the
  // live agent-assist-portal callers in views/**/*.vue — no behavior
  // change. The marker-permission pattern is now implemented inside
  // usePermissions instead of inline here.
  //
  // user_type bypass for tenant-tier admins is preserved: tenant_admin
  // users currently don't have `assist:config:write` directly in their
  // permissions list (they have core/va perms only), but they're
  // expected to keep config access by virtue of being tenant admins.
  const authStore = useAuthStore()
  const {
    canConfigureAssist,
    canViewAssistAnalytics,
    canViewAssistConversations,
  } = usePermissions()

  const isTenantTierAdmin = computed(() =>
    ['super_admin', 'platform_admin', 'tenant_admin'].includes(authStore.user?.user_type ?? '')
  )

  const isAdmin = computed(() =>
    canConfigureAssist.value || isTenantTierAdmin.value
  )
  const isSupervisor = computed(() =>
    canViewAssistAnalytics.value && !isAdmin.value
  )
  const isAgent = computed(() =>
    canViewAssistConversations.value && !isAdmin.value && !isSupervisor.value
  )
  const canConfigure = computed(() => isAdmin.value)
  const canViewAnalytics = computed(() => isAdmin.value || isSupervisor.value)

  const isConfigured = computed(() => config.value !== null)

  const contextMode = computed(() => config.value?.context_mode ?? 'rag')

  const ccaiProfilesEnabled = computed(() =>
    config.value?.ccai_profiles_enabled ?? false
  )

  const isAssistEnabled = computed(() =>
    config.value?.assist_connector_enabled ?? false
  )

  const isAudiohookEnabled = computed(() =>
    config.value?.audiohook_enabled ?? false
  )

  const isMiddlewareEnabled = computed(() =>
    config.value?.middleware_enabled ?? false
  )

  const isPubsubReady = computed(() =>
    config.value?.pubsub_topics_created ?? false
  )

  async function fetchConfig(): Promise<void> {
    loading.value = true
    error.value = null
    try {
      config.value = await assistConfigService.getConfig()
    } catch (e: any) {
      error.value = e?.response?.data?.detail || e?.message || 'Failed to fetch assist configuration'
      console.error('Failed to fetch assist config:', e)
    } finally {
      loading.value = false
    }
  }

  async function updateConfig(data: AssistConfigUpdate): Promise<boolean> {
    loading.value = true
    error.value = null
    try {
      config.value = await assistConfigService.updateConfig(data)
      return true
    } catch (e: any) {
      error.value = e?.response?.data?.detail || e?.message || 'Failed to update assist configuration'
      console.error('Failed to update assist config:', e)
      return false
    } finally {
      loading.value = false
    }
  }

  function reset(): void {
    config.value = null
    loading.value = false
    error.value = null
  }

  return {
    config,
    loading,
    error,
    // Mode
    assistMode,
    isOmniAssist,
    isGoogleNative,
    // Roles
    isAdmin,
    isSupervisor,
    isAgent,
    canConfigure,
    canViewAnalytics,
    // Config state
    ccaiProfilesEnabled,
    isConfigured,
    contextMode,
    isAssistEnabled,
    isAudiohookEnabled,
    isMiddlewareEnabled,
    isPubsubReady,
    // Actions
    fetchConfig,
    updateConfig,
    reset,
  }
})
